import { Attachment } from "@/api/tickets";

/** The fields classification needs; lets non-ticket payloads (the portal) use these helpers. */
export type AttachmentFile = Pick<Attachment, "fileName" | "mimeType">;

export type AttachmentKind =
    | "image"
    | "video"
    | "audio"
    | "pdf"
    | "document"
    | "spreadsheet"
    | "presentation"
    | "archive"
    | "code"
    | "text"
    | "email"
    | "file";

const EXTENSION_KINDS: Record<string, AttachmentKind> = {
    doc: "document",
    docx: "document",
    odt: "document",
    rtf: "document",
    xls: "spreadsheet",
    xlsx: "spreadsheet",
    ods: "spreadsheet",
    csv: "spreadsheet",
    tsv: "spreadsheet",
    ppt: "presentation",
    pptx: "presentation",
    pps: "presentation",
    ppsx: "presentation",
    odp: "presentation",
    zip: "archive",
    gz: "archive",
    tgz: "archive",
    bz2: "archive",
    xz: "archive",
    tar: "archive",
    rar: "archive",
    "7z": "archive",
    json: "code",
    xml: "code",
    yml: "code",
    yaml: "code",
    sql: "code",
    ts: "code",
    js: "code",
    cs: "code",
    py: "code",
    sh: "code",
    html: "code",
    css: "code",
    txt: "text",
    log: "text",
    md: "text",
    pdf: "pdf",
};

const KIND_ICONS: Record<AttachmentKind, string> = {
    image: "mdi-file-image",
    video: "mdi-file-video",
    audio: "mdi-file-music",
    pdf: "mdi-file-pdf-box",
    document: "mdi-file-word-box",
    spreadsheet: "mdi-file-excel-box",
    presentation: "mdi-file-powerpoint-box",
    archive: "mdi-zip-box",
    code: "mdi-code-json",
    text: "mdi-text-box",
    email: "mdi-email",
    file: "mdi-file",
};

export function attachmentExtension(fileName: string): string {
    const parts = fileName.split(".");
    return parts.length > 1 ? (parts.pop() ?? "").toLowerCase() : "";
}

// The MIME type is authoritative when we have one, but attachments imported before it was
// recorded - and anything the browser typed as octet-stream - still need the extension.
export function attachmentKind(attachment: AttachmentFile): AttachmentKind {
    const mimeType = attachment.mimeType.toLowerCase();

    // Before the MIME checks: the upload sniffer has no signature for .eml (octet-stream) and
    // reads OLE .msg files as Office documents.
    const extension = attachmentExtension(attachment.fileName);
    if (extension === "eml" || extension === "msg") return "email";

    if (mimeType === "application/pdf") return "pdf";
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";

    const byExtension = EXTENSION_KINDS[attachmentExtension(attachment.fileName)];
    if (byExtension) return byExtension;

    return mimeType.startsWith("text/") ? "text" : "file";
}

export function attachmentIcon(attachment: AttachmentFile): string {
    return KIND_ICONS[attachmentKind(attachment)];
}

export function isPlayable(attachment: AttachmentFile): boolean {
    const kind = attachmentKind(attachment);
    return kind === "video" || kind === "audio";
}

export function isEmail(attachment: AttachmentFile): boolean {
    return attachmentKind(attachment) === "email";
}
